import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import PageHeader from 'components/atoms/PageHeader'
import CreateBpForm from 'features/createBp/components/CreateBpForm'
import Summary from 'features/createBp/components/Summary'
import { useDispatch, useSelector } from 'react-redux'
import { clearCreatedBpRoutine } from 'features/createBp/ducks/actions'
import { selectCreatedBp } from 'features/createBp/ducks/selectors'
import { isEmpty } from 'ramda'
import { clearBpFilesRoutine } from 'ducks/files/actions'
import { clearBpPricesRoutine } from 'features/bpList/ducks/actions'

const CreateBp = () => {
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState('basic')
  const [activeStep, setActiveStep] = useState('building-info')
  const createdBp = useSelector(selectCreatedBp)
  const [bp, setBp] = useState({})
  const [completed, setCompleted] = useState({
    buildingInfo: false,
    buildingDetails: false,
    relationship: false,
    floorNumber: false,
    residents: false,
    issues: false,
    reminders: false
  })
  const [startedSections, setStartedSections] = useState({
    basic: true,
    floors: false,
    details: false
  })

  useEffect(() => {
    if (!createdBp || isEmpty(createdBp)) {
      return
    }

    setBp(prev => {
      if (isEmpty(prev)) {
        return createdBp
      }

      if (!prev?.id || prev.id !== createdBp.id) {
        return {
          ...prev,
          ...createdBp
        }
      }

      return prev
    })
  }, [createdBp?.id])

  useEffect(() => {
    dispatch(clearBpFilesRoutine())
    dispatch(clearCreatedBpRoutine())
    dispatch(clearBpPricesRoutine())
  }, [])

  const markAsCompleted = type => {
    setCompleted(prev => ({
      ...prev,
      [type]: true
    }))
  }

  const setStarted = type => {
    setStartedSections(prev => ({
      ...prev,
      [type]: true
    }))
  }

  const handleChangeTab = (tabName, started) => () => {
    if (started) {
      switch (tabName) {
        case 'basic':
          setActiveTab('basic')
          setActiveStep('building-info')
          break
        case 'floors':
          setActiveTab('floors')
          setActiveStep('floor-number')
          break
        case 'details':
          setActiveTab('details')
          setActiveStep('residents')
          break
      }
    }
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <PageHeader>Create Building Passport</PageHeader>
        <WizardContent>
          <FormSteps>
            <FormStep
              started={startedSections.basic}
              onClick={handleChangeTab('basic', startedSections.basic)}
              active={activeTab === 'basic'}
            >
              1. Basic information
            </FormStep>
            <FormStep
              started={startedSections.floors}
              onClick={handleChangeTab('floors', startedSections.floors)}
              active={activeTab === 'floors'}
            >
              2. Floors
            </FormStep>
            <FormStep
              started={startedSections.details}
              onClick={handleChangeTab('details', startedSections.details)}
              active={activeTab === 'details'}
            >
              3. Additional details
            </FormStep>
          </FormSteps>
          <CreateBpForm
            completed={completed}
            setCompleted={markAsCompleted}
            activeTab={activeTab}
            activeStep={activeStep}
            setActiveTab={setActiveTab}
            setStarted={setStarted}
            setActiveStep={setActiveStep}
            setBp={setBp}
            bp={bp}
          />
        </WizardContent>
      </ContentWrapper>
      <Sidebar>
        <Summary bp={bp} />
      </Sidebar>
    </PageWrapper>
  )
}

export default CreateBp

const PageWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  justify-content: center;
  padding-right: 300px;
  position: relative;
  @media (max-width: 1024px) {
    padding-right: 0;
    flex-direction: column;
    overflow-x: hidden;
  }
`

const ContentWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;

  @media (max-width: 768px) {
    padding-bottom: 20px;
  }
`

const WizardContent = styled.div`
  width: min(1480px, calc(100vw - 460px));
  box-sizing: border-box;
  margin: 0 auto;

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 1480px;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    padding: 0 4px;

    & * {
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
  }
`

const Sidebar = styled.div`
  width: 280px;
  position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  padding: 25px 20px;
  overflow-y: auto;
  z-index: 10;

  @media (max-width: 1024px) {
    position: static;
    width: 100%;
    height: auto;
    margin-top: 40px;
    padding-bottom: 120px;
    border-top: 1px solid #e0e0e0;
  }
`

const FormSteps = styled.div`
  width: 100%;
  display: flex;
  font-size: 20px;
  font-weight: 600;
  margin: 15px 0 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    font-size: 16px;
    flex-direction: column;
    gap: 8px;

    background: #f9f9f9;
    padding: 15px;
    border-radius: 8px;
  }
`

const FormStep = styled.div`
  color: ${({ theme, active }) =>
    active ? theme.colors.secondary : theme.colors.grey[600]};

  &:not(:last-of-type) {
    margin-right: 30px;

    @media (max-width: 768px) {
      margin-right: 0;
    }
  }

  ${({ started, active }) =>
    started &&
    !active &&
    css`
      cursor: pointer;

      &:hover {
        color: ${({ theme, active }) =>
          active ? theme.colors.secondary : theme.colors.grey[700]};
      }
    `}

  ${({ active, theme }) =>
    active &&
    css`
      @media (max-width: 768px) {
        color: ${theme.colors.secondary};
        padding-left: 10px;
        border-left: 3px solid ${theme.colors.secondary};
      }
    `}
`
